import React, { useMemo } from "react";

import { ReactComponent as ChromeIcon } from "@common/assets/images/billing/chrome-icon.svg";
import { ReactComponent as CodeIcon } from "@common/assets/images/billing/code-icon.svg";
import { ReactComponent as TeamMembersIcon } from "@common/assets/images/billing/team-members.svg";
import { ReactComponent as UniqueDevicesIcon } from "@common/assets/images/billing/unique-devices.svg";
import { ReactComponent as Warning } from "@common/assets/images/billing/warning-icon.svg";
import { AdministrationCard } from "@common/components/AdministrationCard/AdministrationCard";
import Button from "@common/components/Button/Button";
import { BaseDialog, useBaseDialog } from "@common/components/Dialog/BaseDialog";
import { CancelSubscriptionBody } from "@common/components/Dialog/configurations/CancelSubscriptionBody";
import { useModal } from "@common/components/Modal/Modal";
import Paragraph from "@common/components/Paragraph/Paragraph";
import { GroupedOptions, Option } from "@common/components/SelectDropdown/SelectDropdown";
import Tooltip from "@common/components/Tooltip/Tooltip";
import { SdkInitializationsTimeData } from "@common/models/Analytics.model";
import { Customer, Plan, PlanInterval, PlanType } from "@common/models/billing";
import { isAdmin } from "@common/models/Member.model";
import { vars } from "@common/styles";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import { getBillableApps } from "@common/util/appsUtil";
import { helpCenterArticles } from "@common/util/helpCenterArticles";
import identifiers from "@common/util/identifiers.json";
import { showIntercomArticle } from "@common/util/intercom";
import { quantity } from "@common/util/strings";
import { useAppContext } from "@main/context/App/App.context";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import {
  FREE_PLAN_SDK_INITS_LIMIT_STRING,
  getAddOnSdkInitializationsLimitString,
  getAppsPlanLimit,
  getTeamMembersPlanLimit,
  isAppsNumberLimitReached,
  isSdkInitializationsLimitReached,
  isTeamMembersLimitReached,
} from "@main/context/Subscription/SubscriptionContext";
import { StackedBarChart } from "@main/pages/Release/components/Charts/StackedBarChart";
import { RoutePaths } from "@main/router/config/routePaths";
import { generatePath } from "react-router-dom";

import * as Styled from "./YourSubscriptionCard.styles";
import { useSubscriptionFormConsumer } from "../../../consumers/useSubscriptionFormConsumer";
import { ChangeSubscriptionModalBody } from "../../ModalBody/Subscriptions/ChangeSubscriptionModalBody";

const defaultPlanValue = { label: "Free plan", value: "", subtitle: "$0 forever" };
const defaultPlanAddOnValue = {
  label: `${FREE_PLAN_SDK_INITS_LIMIT_STRING} SDK initializations`,
  value: "",
  subtitle: "$0 forever",
} as Option;

interface Props {
  plans: Plan[];
  addOns: Plan[];
  sdkInitializationsData?: SdkInitializationsTimeData[];
  isSdkInitializationsDataError: boolean;
  isSdkInitializationsDataLoading: boolean;
  customer?: Customer;
  cancelSubscription: () => Promise<void>;
}

export function YourSubscriptionCard({
  plans,
  addOns,
  sdkInitializationsData,
  isSdkInitializationsDataError,
  isSdkInitializationsDataLoading,
  customer,
  cancelSubscription,
}: Props) {
  const { apps, wsTeamMembers } = useAppContext();
  const { selectedWorkspace } = useAppSelectionContext();
  const isUserAdmin = isAdmin(selectedWorkspace?.role);

  const activeApps = useMemo(() => getBillableApps(apps), [apps]);

  const {
    Modal: SubscriptionChangeModal,
    toggle: toggleChangeSubscription,
    modalProps: subscriptionChangeInternal,
  } = useModal({ size: "full" });

  const cancelModal = useBaseDialog({});

  const { formState, handleFormChange, handleFormSubmit, loading } = useSubscriptionFormConsumer({
    customer,
    toggleChangeSubscription,
    plans,
    addOns,
  });

  const optionDidChange = (option: Option, name: string | undefined) => {
    name && handleFormChange(name, option);

    if (name === "plan" && option.label === "Free plan") {
      handleFormChange("addOn", defaultPlanAddOnValue);
    }

    if (name === "plan" && formState.addOn.value) {
      const chosenPlan = plans.find((plan) => plan.id === option.value) as Plan;
      const chosenAddOn = addOns.find((plan) => plan.id === formState.addOn.value) as Plan;

      if (chosenPlan && chosenPlan.interval !== chosenAddOn.interval) {
        const addOnToMatch = addOns.find(
          (addOn) => addOn.product_name === chosenAddOn.product_name && addOn.interval === chosenPlan.interval,
        );

        if (addOnToMatch) {
          const addOnToMatchOption = {
            value: addOnToMatch?.id,
            label: addOnToMatch.product_name,
            subtitle: getPlanSubtitle(addOnToMatch),
          };
          handleFormChange("addOn", addOnToMatchOption);
        }
      }
    }
  };

  const isDataUnchanged =
    (Boolean(customer?.subscription?.items.find((item) => item.plan.id === formState.plan.value)) &&
      Boolean(customer?.subscription?.items.find((item) => item.plan.id === formState.addOn.value))) ||
    (Boolean(customer?.subscription?.items.find((item) => item.plan.id === formState.plan.value)) &&
      formState.addOn.value === "" &&
      !Boolean(customer?.subscription?.items.find((item) => item.plan.type === "devices"))) ||
    (formState.addOn.value === "" &&
      formState.plan.value === "" &&
      !Boolean(customer?.subscription?.items.find((item) => item.plan.type === "devices")) &&
      !Boolean(customer?.subscription?.items.find((item) => item.plan.type === "plan")));
  const canUserEdit = isUserAdmin || (!isUserAdmin && !Boolean(customer?.subscription));

  const isButtonDisabled = !formState.plan.value;

  const tooltipText = customer?.subscription?.cancel_at_period_end
    ? "Please resume subscription first"
    : !canUserEdit
    ? "Only admins can change the subscription."
    : isButtonDisabled
    ? "Please choose a plan first"
    : isDataUnchanged
    ? "No changes have been made"
    : "";

  const teamMembersNum: number = wsTeamMembers?.length ?? 0;
  const activeChromeExtensionAppsNum: number = activeApps.filter((a) => a.is_extension)?.length ?? 0;
  const activeSdkEmbeddedAppsNum: number = activeApps.filter((a) => !a.is_extension)?.length ?? 0;

  const uniqueDevicesNum: number = selectedWorkspace?.unique_devices ?? 0;

  const currentPlan = customer?.subscription?.items.find((a) => a.plan.type === PlanType.PLAN)?.plan;
  const currentAddOn = customer?.subscription?.items.find((a) => a.plan.type === PlanType.DEVICES)?.plan;

  const selectedPlan = plans.find((a) => a.id === formState.plan.value);
  const selectedAddOn = addOns.find((a) => a.id === formState.addOn.value);

  return (
    <AdministrationCard
      title="Your subscription"
      testId={identifiers["subscription.header.yourSubscription"]}
    >
      {/* Header helper texts */}
      <Styled.Helper $noMargin={true}>
        <Paragraph>
          {"Read all about Shake plans, startup discounts and add-ons on our "}
          <a
            href="https://www.shakebugs.com/pricing/"
            target="_blank"
            rel="noreferrer"
          >
            {"website"}
          </a>
          {".\n"}
        </Paragraph>
        <Paragraph>
          {"Running a tiny startup? "}
          <a
            href="https://www.shakebugs.com/startups/"
            target="_blank"
            rel="noreferrer"
          >
            {"Apply for a discount"}
          </a>
          {". If approved, follow the instructions you receive.\n"}
        </Paragraph>
        <Paragraph>
          {"Need 100+ seats, 20+ apps, or more? "}
          <a href="mailto:friends@shakebugs.com">{"Email us"}</a>
          {" about the Enterprise plan."}
        </Paragraph>
      </Styled.Helper>

      {/* Plans header */}
      <Paragraph
        fontWeight="bold"
        color={vars.colors.grey30}
        marginBottom={10}
      >
        {"Plan"}
      </Paragraph>

      {/* Team members limit info*/}
      <Flex
        $alignItems="flex-start"
        $gap={0.4}
        style={{ paddingBottom: "1.2rem" }}
      >
        {isTeamMembersLimitReached(wsTeamMembers, selectedPlan) ? (
          <Tooltip
            style={{ width: "auto", cursor: "help", display: "flex" }}
            text={`Your plan allows up to ${getTeamMembersPlanLimit(currentPlan)} ${quantity(
              getTeamMembersPlanLimit(currentPlan),
              "team member",
              "team members",
            )}`}
            position={"top"}
          >
            <Warning />
          </Tooltip>
        ) : (
          <TeamMembersIcon />
        )}

        <Styled.HeadingSubtitle fontWeight={500}>
          {`${teamMembersNum} `}
          <Styled.LinkText
            to={generatePath(RoutePaths.ADMINISTRATION_MEMBERS, {
              workspaceSlug: selectedWorkspace?.slug ?? null,
            })}
          >
            {`${quantity(teamMembersNum, "team member", "team members")}`}
          </Styled.LinkText>
          {` in this workspace`}
        </Styled.HeadingSubtitle>
      </Flex>

      {/* Apps limit info*/}
      <Flex
        $alignItems="flex-start"
        $gap={0.4}
        style={{ paddingBottom: "1.2rem" }}
      >
        {isAppsNumberLimitReached(activeApps, selectedPlan) ? (
          <Tooltip
            style={{ width: "auto", cursor: "help", display: "flex" }}
            text={`Your plan allows up to ${getAppsPlanLimit(currentPlan)} active ${quantity(
              getAppsPlanLimit(currentPlan),
              "app",
              "apps",
            )}`}
            position={"top"}
          >
            <Warning />
          </Tooltip>
        ) : (
          <CodeIcon />
        )}

        <Styled.HeadingSubtitle fontWeight={500}>
          {`${activeSdkEmbeddedAppsNum} active `}
          <Styled.LinkText
            to={generatePath(RoutePaths.ADMINISTRATION_APPS_GENERAL, {
              workspaceSlug: selectedWorkspace?.slug ?? null,
            })}
          >
            {`${quantity(activeSdkEmbeddedAppsNum, "app", "apps")}`}
          </Styled.LinkText>
          {` with the SDK - to lower this, deactivate some of them`}
        </Styled.HeadingSubtitle>
      </Flex>

      {/* Chrome extension limit info */}
      <Flex
        $alignItems="center"
        $gap={0.4}
      >
        <ChromeIcon />
        <Styled.HeadingSubtitle fontWeight={500}>
          {`${activeChromeExtensionAppsNum} active Chrome extension ${quantity(
            activeChromeExtensionAppsNum,
            "app",
            "apps",
          )}`}
        </Styled.HeadingSubtitle>
        <Styled.FreeAndUnlimitedNote>{"Free & unlimited"}</Styled.FreeAndUnlimitedNote>
      </Flex>

      {/* Plan selection dropdown */}
      <Tooltip
        text={tooltipText}
        disabled={(canUserEdit && !customer?.subscription?.cancel_at_period_end) || loading}
        position="top"
        offset={0}
      >
        <Styled.SelectDropdown
          label="Plan"
          isSearchable={false}
          defaultValue={defaultPlanValue}
          groupedOptions={getGroupedOptions(getPlansAsOptions(plans, PlanType.PLAN))}
          disabled={!canUserEdit || loading || customer?.subscription?.cancel_at_period_end}
          value={formState.plan}
          name="plan"
          onChange={optionDidChange}
          testId={identifiers["subscription.currentPlan.button"]}
          hasSubtitles
        />
      </Tooltip>

      {/* Add-on header */}
      <Paragraph
        fontWeight="bold"
        color={vars.colors.grey30}
        marginBottom={10}
        style={{ marginTop: "1.2rem" }}
      >
        Add-on
      </Paragraph>
      <Styled.Helper $noMargin={true}>
        <Paragraph>
          All commonly asked questions about add-ons are{" "}
          <a onClick={() => showIntercomArticle(helpCenterArticles.sdkInitializationsAndAddons)}>here</a>.
        </Paragraph>
      </Styled.Helper>

      {/* Add-on limit info */}
      <Flex
        $alignItems="center"
        $gap={0.4}
      >
        {isSdkInitializationsLimitReached(selectedWorkspace, selectedAddOn) ? (
          <Tooltip
            style={{ width: "auto", cursor: "help", display: "flex" }}
            text={`Your current add-on includes ${getAddOnSdkInitializationsLimitString(
              currentAddOn,
            )} SDK initializations`}
            position={"top"}
          >
            <Warning />
          </Tooltip>
        ) : (
          <UniqueDevicesIcon />
        )}
        <Styled.HeadingSubtitle fontWeight={500}>
          {`${uniqueDevicesNum} SDK initializations in active apps - to lower this, deactivate some of the `}
          <Styled.LinkText
            to={generatePath(RoutePaths.ADMINISTRATION_APPS_GENERAL, {
              workspaceSlug: selectedWorkspace?.slug ?? "",
            })}
          >
            {"apps"}
          </Styled.LinkText>
        </Styled.HeadingSubtitle>
      </Flex>

      <StackedBarChart
        style={{ height: "19.4rem", marginTop: "1.6rem" }}
        data={sdkInitializationsData ?? []}
        error={isSdkInitializationsDataError}
        loading={isSdkInitializationsDataLoading}
      />

      {/* Add-on selection dropdown */}
      <Tooltip
        text={tooltipText}
        disabled={(canUserEdit && !customer?.subscription?.cancel_at_period_end && !isButtonDisabled) || loading}
        position="top"
        offset={0}
      >
        <Styled.SelectDropdown
          label="Add-on"
          isSearchable={false}
          defaultValue={defaultPlanAddOnValue}
          disabled={isButtonDisabled || !canUserEdit || loading || customer?.subscription?.cancel_at_period_end}
          groupedOptions={getGroupedOptions(
            [defaultPlanAddOnValue].concat(getAddOnOptionsPerInterval(addOns, formState.plan, plans) ?? []),
          )}
          value={formState.addOn}
          name="addOn"
          onChange={optionDidChange}
          testId={identifiers["subscription.addon.button"]}
          hasSubtitles
        />
      </Tooltip>

      {/* Submit subscription settings button */}
      <Styled.Flex>
        <Button
          type="submit"
          size="small"
          disabled={!canUserEdit || isDataUnchanged || loading}
          onClick={() =>
            Boolean(customer?.subscription) && formState.plan.value === "" && formState.addOn.value === ""
              ? cancelModal.setOpen(true)
              : !Boolean(customer?.subscription)
              ? handleFormSubmit()
              : toggleChangeSubscription()
          }
          tooltip={{
            text: tooltipText,
            position: "top",
            disabled: canUserEdit && !isDataUnchanged,
          }}
          testId={identifiers["subscription.button.proceed"]}
          loadingText={loading && "Proceeding..."}
        >
          Preview the cost breakdown
        </Button>
        <Styled.ButtonNote p2>No worries, clicking this button does not charge you</Styled.ButtonNote>
      </Styled.Flex>

      {/* Modals */}
      <SubscriptionChangeModal
        {...subscriptionChangeInternal}
        heading="Subscription update"
        testId={identifiers["subscription.modal.header.change"]}
        buttonElement={
          <Button
            onClick={handleFormSubmit}
            disabled={loading}
            size="small"
            testId={identifiers["subscription.modal.button.change"]}
            loadingText={loading && "Confirming..."}
          >
            Confirm
          </Button>
        }
      >
        <ChangeSubscriptionModalBody
          currentPlan={currentPlan}
          currentAddOn={currentAddOn}
          newPlan={plans.find((plan) => plan.id === formState.plan.value)}
          newAddOn={addOns.find((a) => a.id === formState.addOn.value)}
        />
      </SubscriptionChangeModal>
      <BaseDialog {...cancelModal}>
        <CancelSubscriptionBody
          onCancel={() => {
            cancelModal.setOpen(false);
            cancelSubscription();
          }}
        />
      </BaseDialog>
    </AdministrationCard>
  );
}

const getGroupedOptions = (options: Option[]) => {
  const free = {
    label: "",
    options: options.filter((a) => a.value === ""),
  } as GroupedOptions;

  const yearly = {
    label: "Annual billing • Save 20%",
    options: options.filter(
      (a) => a.subtitle?.includes(`once a ${PlanInterval.WEEK}`) || a.subtitle?.includes(`once a ${PlanInterval.YEAR}`),
    ),
  } as GroupedOptions;

  const monthly = {
    label: "Monthly billing",
    options: options.filter(
      (a) => a.subtitle?.includes(`once a ${PlanInterval.DAY}`) || a.subtitle?.includes(`once a ${PlanInterval.MONTH}`),
    ),
  } as GroupedOptions;

  return [free].concat([monthly]).concat([yearly]);
};

const getAddOnOptionsPerInterval = (addOns: Plan[], chosenPlanOption: Option, plans: Plan[]) => {
  if (!chosenPlanOption.value) {
    return getPlansAsOptions(addOns, PlanType.DEVICES);
  }

  const chosenPlan: Plan | undefined = plans.find((plan) => plan.id === chosenPlanOption.value);
  const intervalPlans: Plan[] = addOns.filter((plan) => plan.interval === chosenPlan?.interval);
  return getPlansAsOptions(intervalPlans, PlanType.DEVICES);
};

const getPlansAsOptions = (plans: Plan[], type: PlanType) => {
  plans.sort((a, b) => a.amount - b.amount);

  const free = { label: "Free plan", value: "", subtitle: "$0 forever" } as Option;
  const planOptions = plans.map((plan) => {
    return {
      label: plan.product_name,
      value: plan.id,
      subtitle: getPlanSubtitle(plan),
    } as Option;
  });

  if (type === PlanType.PLAN) return [free].concat(planOptions);
  return planOptions;
};

export const getPlanSubtitle = (plan: Plan) => {
  const amountPerMonth =
    plan.interval === PlanInterval.YEAR || plan.interval === PlanInterval.WEEK ? plan.amount / 12 : plan.amount;
  return `$${amountPerMonth.toString().slice(0, -2)} per month \u00A0\u00A0\u00A0\u00A0Billed once a ${
    plan.interval
  } as $${plan.amount.toString().slice(0, -2)}`;
};
